import React from 'react';
import { formatNumber, formatDifference, formatRank } from '../../utils/formatting';
import { getValueColor } from '../../utils/coloring';
import { sortData, requestSort, getSortIcon } from '../../utils/sorting';
import { getCrimeDisplayName } from '../../constants/crimes';
import { getStateAbbreviation, isStateScopedCityCountyAggregate } from '../../constants/locations';
import { useMenuState, useMobileState } from '../../hooks/tables';
import { ChevronDown } from 'lucide-react';
import './Tables.css';
import './LocationType.css';

const LocationTypeTotalsTable = ({ 
  data,
  locationType,
  onLocationSelect,
  selectedLocation,
  selectedCrime,
  sortConfig,
  onSortChange,
  currentPage,
  itemsPerPage,
  compareMode,
  onCompareModeChange,
  setHoveredLocation
}) => {
  const { menuOpen, setMenuOpen, toggleMenu } = useMenuState();
  const isMobile = useMobileState();

  const isMetro = locationType === 'metro';
  const isCounty = locationType === 'county';
  const isCity = locationType === 'city';
  const isCountyOrCity = isCounty || isCity;
  const isOneCountyOrCity = data?.locations?.[0]?.total_counties_state === 1 || data?.locations?.[0]?.total_cities_state === 1; 
  const isAllCountiesNational = isCounty && selectedLocation?.info?.name === 'All Counties';
  const isAllCitiesNational = isCity && selectedLocation?.info?.name === 'All Cities';
  const isAllNational = isAllCountiesNational || isAllCitiesNational;
  
  const selectedState = selectedLocation?.info?.state;
  const dataState = data?.locations?.[0]?.state;
  const dataHasMultipleStates = data?.locations?.length > 1 && 
                                data.locations.some(loc => loc.state !== data.locations[0].state);
  const dataIsNational = isCountyOrCity && dataHasMultipleStates;
  const selectedIsNational = !selectedState;
  const bothAreState = selectedState && dataState && !dataIsNational;
  const bothAreNational = selectedIsNational && dataIsNational;
  const dataMatchesSelection = bothAreNational || bothAreState || (selectedState === dataState);
  const isStateScopedAggregate = isStateScopedCityCountyAggregate(selectedLocation);

  const showComparisonDropdown =
    isCountyOrCity &&
    !isOneCountyOrCity &&
    !isAllNational &&
    (dataMatchesSelection || (isStateScopedAggregate && !dataIsNational));

  const locationPrefix = locationType.charAt(0).toUpperCase() + locationType.slice(1);
  const crimePrefix = selectedCrime.toLowerCase();  
  
  let stateAbbr = '';
  if (isCountyOrCity && data?.locations?.length > 0) {
    stateAbbr = getStateAbbreviation(data.locations[0]?.state || '');
  }

  const sortedData = sortData(data.locations, sortConfig);
  const currentRows = sortedData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleSort = (key) => requestSort(key, sortConfig, onSortChange);
  
  const handleCompareModeChange = (mode) => {
    onCompareModeChange(mode);
    setMenuOpen(false);
    
    onSortChange({
      key: `${crimePrefix}_count`,
      direction: 'desc'
    });
  };

  const rankField = isCountyOrCity
    ? compareMode === 'state'
      ? `${crimePrefix}_state_count_rank`
      : `${crimePrefix}_national_count_rank`
    : `${crimePrefix}_count_rank`;
  
  const avgField = isCountyOrCity
    ? compareMode === 'state'
      ? `${crimePrefix}_state_count_avg`
      : `${crimePrefix}_national_count_avg`
    : `${crimePrefix}_national_count_avg`;
    
  const avgDiffField = isCountyOrCity
    ? compareMode === 'state'
      ? `${crimePrefix}_state_count_avg_diff`
      : `${crimePrefix}_national_count_avg_diff`
    : `${crimePrefix}_national_count_avg_diff`;
    
  const avgDiffPctField = isCountyOrCity
    ? compareMode === 'state'
      ? `${crimePrefix}_state_count_avg_diff_pct`
      : `${crimePrefix}_national_count_avg_diff_pct`
    : `${crimePrefix}_national_count_avg_diff_pct`;

  const formatLocationName = (location) => {
    if (isCounty) {
      return (location.county === 'Carson City' && location.state === 'Nevada') ?
        `${location.county}, ${getStateAbbreviation(location.state)}` :
        location.state === 'Louisiana' ?
          `${location.county} Parish, ${getStateAbbreviation(location.state)}` :
        location.state === 'Alaska' ?
          `${location.county} Borough, ${getStateAbbreviation(location.state)}` :
          `${location.county} County, ${getStateAbbreviation(location.state)}`;
    } else if (isCity) {
      return `${location.city}, ${getStateAbbreviation(location.state)}`;
    } else {
      return location.name;
    }
  };

  const formatLocationNameForSelection = (location) => {
    if (isCounty) {
      return (location.county === 'Carson City' && location.state === 'Nevada') ?
        `${location.county}, ${location.state}` :
        location.state === 'Louisiana' ?
          `${location.county} Parish, ${location.state}` :
        location.state === 'Alaska' ?
          `${location.county} Borough, ${location.state}` :
          `${location.county} County, ${location.state}`;
    } else if (isCity) {
      return `${location.city}, ${location.state}`;
    } else {
      return location.name;
    }
  };

  const getLocationSelectData = (location) => {
    const locationName = formatLocationNameForSelection(location);
    
    return {
      name: locationName,
      type: locationType,
      info: {
        name: locationName,
        type: locationType,
        ...((isCounty || isCity) && { state: location.state })
      }
    };
  };

  return (
    <div className="main-table-wrapper">
      <div className="main-table-top">
        <div className="table-top-left">
          {showComparisonDropdown ? (
            <div className="comparison-container">
              <div className="comparison-menu-container">
                <button 
                  className={`comparison-menu-button ${menuOpen ? 'menu-open' : ''}`} 
                  onClick={toggleMenu}
                >
                  <ChevronDown size={16} />
                </button>
                {menuOpen && (
                  <div className="comparison-menu">
                    <button 
                      className={compareMode === 'state' ? 'active' : ''}
                      onClick={() => handleCompareModeChange('state')}
                      disabled={compareMode === 'state'}
                    >
                      {stateAbbr} {locationPrefix}
                    </button>
                    <button 
                      className={compareMode === 'national' ? 'active' : ''}
                      onClick={() => handleCompareModeChange('national')}
                      disabled={compareMode === 'national'}
                    >
                      US {locationPrefix}
                    </button>
                  </div>
                )}
              </div>
              <span>
                {compareMode === 'state' ? stateAbbr : 'US'} {locationPrefix} {getCrimeDisplayName(selectedCrime)} Average: <strong>{formatNumber(data.locations[0]?.[avgField])}</strong>
              </span>
            </div>
          ) : (
            <div>
              {(isAllNational || isOneCountyOrCity || dataIsNational) ? 'US ' : ''}{locationPrefix} {getCrimeDisplayName(selectedCrime)} Average: <strong>{formatNumber(data.locations[0]?.[avgField])}</strong>
            </div>
          )}
        </div>
      </div>
      <div className="main-table-scroll-container">
        <table className={`main-table location-type-table ${(isCountyOrCity || isMetro) ? locationType : ''} totals`}>
          <thead>
            <tr>
              <th onClick={() => handleSort(rankField)} className="sortable">
                {isCountyOrCity && isMobile ? (
                  <>
                    <div>Rank</div>
                    {getSortIcon(rankField, sortConfig)}
                  </>
                ) : (
                  <div className="stacked-header">
                    <div>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationPrefix}</div>
                    <div>Rank</div>
                    {getSortIcon(rankField, sortConfig)}
                  </div>
                )}
              </th>
              <th onClick={() => handleSort('name')} className="sortable">
                {locationPrefix}
                {getSortIcon('name', sortConfig)}
              </th>
              <th onClick={() => handleSort(`${crimePrefix}_count`)} className="sortable">
                <div className="stacked-header">
                  <div>Total</div>
                  <div>{getCrimeDisplayName(selectedCrime)}</div>
                  {getSortIcon(`${crimePrefix}_count`, sortConfig)}
                </div>
              </th>
              <th onClick={() => handleSort(avgDiffField)} className="sortable">
                <div className="stacked-header">
                  <div>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationPrefix} Avg</div>
                  <div>Difference</div>
                  {getSortIcon(avgDiffField, sortConfig)}
                </div>
              </th>
              <th onClick={() => handleSort(avgDiffPctField)} className="sortable">
                <div className="stacked-header">
                  <div>% {isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationPrefix} Avg</div>
                  <div>Difference</div>
                  {getSortIcon(avgDiffPctField, sortConfig)}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {currentRows.map((location, index) => (
              <tr key={location.name || index}>
                <td className="rank-cell">{formatRank(location[rankField])}</td>
                <td>
                  <span 
                    onMouseEnter={() => setHoveredLocation(formatLocationNameForSelection(location))}
                    onMouseLeave={() => setHoveredLocation(null)}
                    onClick={() => onLocationSelect(getLocationSelectData(location))}
                  >
                    {formatLocationName(location)}
                  </span>
                </td>
                <td className="number-cell bold">{formatNumber(location[`${crimePrefix}_count`])}</td>
                <td className="number-cell" style={{ color: getValueColor(location[avgDiffField]) }}>
                  {formatDifference(location[avgDiffField])}
                </td>
                <td className="number-cell" style={{ color: getValueColor(location[avgDiffPctField]) }}>
                  {formatDifference(location[avgDiffPctField], true)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LocationTypeTotalsTable;